import React from 'react';
import {
  ChevronDown, Image as ImageIcon,
  Lightbulb, LockKeyhole, Play,
  type LucideIcon,
} from 'lucide-react';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {RootState} from '../../store';
import {
  AnalyticsApiError, CreatorAnalytics, CreatorAnalyticsContentItem,
  CreatorAnalyticsMetric, CreatorAnalyticsPresetRange, CreatorAnalyticsSelection,
  CreatorAnalyticsTrendBucket, analyticsApi,
} from '../../services/analyticsApi';
import {PostReactionType} from '../../services/socialFeedApi';
import {isProPlanTier} from '../../utils/plan';
import {getAssetUrl} from '../../utils/assets';
import {POST_REACTION_BY_TYPE} from './home/constants';
import ProfileWarningIcon from './profile/components/ProfileWarningIcon';
import styles from './analytics/Analytics.module.css';

const RANGE_OPTIONS: Array<{label: string; value: CreatorAnalyticsPresetRange}> = [
  {label: '7D', value: '7d'}, {label: '30D', value: '30d'}, {label: '90D', value: '90d'},
];
const analyticsHeaderIconStyle = {
  '--analytics-header-icon-url': `url("${process.env.PUBLIC_URL}/icons/analytics-fill.svg")`,
} as React.CSSProperties;
const analyticsCalendarIconStyle = {
  '--analytics-calendar-icon-url': `url("${process.env.PUBLIC_URL}/icons/calendar-filled.svg")`,
} as React.CSSProperties;
const analyticsDateInputIconStyle = {
  '--analytics-date-input-icon-url': `url("${process.env.PUBLIC_URL}/icons/calendar-fill.svg")`,
} as React.CSSProperties;
const analyticsExportIconStyle = {
  '--analytics-export-icon-url': `url("${process.env.PUBLIC_URL}/icons/download-rounded.svg")`,
} as React.CSSProperties;
const analyticsIncreaseIconStyle = {
  '--analytics-change-icon-url': `url("${process.env.PUBLIC_URL}/icons/increase-24.svg")`,
} as React.CSSProperties;
const analyticsDecreaseIconStyle = {
  '--analytics-change-icon-url': `url("${process.env.PUBLIC_URL}/icons/decrease-24.svg")`,
} as React.CSSProperties;
const analyticsTrendUpIconStyle = {
  '--analytics-metric-icon-url': `url("${process.env.PUBLIC_URL}/icons/trend-up-fill.svg")`,
} as React.CSSProperties;
const analyticsTrendHeaderIconStyle = {
  '--analytics-header-icon-url': `url("${process.env.PUBLIC_URL}/icons/trend-up-fill.svg")`,
} as React.CSSProperties;
const analyticsReactionMixHeaderIconStyle = {
  '--analytics-header-icon-url': `url("${process.env.PUBLIC_URL}/icons/emoticon-happy-outline.svg")`,
} as React.CSSProperties;
const analyticsBestTimeHeaderIconStyle = {
  '--analytics-header-icon-url': `url("${process.env.PUBLIC_URL}/icons/time-alt.svg")`,
} as React.CSSProperties;
const analyticsTopContentHeaderIconStyle = {
  '--analytics-header-icon-url': `url("${process.env.PUBLIC_URL}/icons/ranking-fill.svg")`,
} as React.CSSProperties;
const analyticsUserGroupIconStyle = {
  '--analytics-metric-icon-url': `url("${process.env.PUBLIC_URL}/icons/user-group.svg")`,
} as React.CSSProperties;
const analyticsServiceClicksIconStyle = {
  '--analytics-metric-icon-url': `url("${process.env.PUBLIC_URL}/icons/click-filled.svg")`,
} as React.CSSProperties;
const analyticsNewFollowersIconStyle = {
  '--analytics-metric-icon-url': `url("${process.env.PUBLIC_URL}/icons/user-add-fill.svg")`,
} as React.CSSProperties;
const analyticsPostsPublishedIconStyle = {
  '--analytics-metric-icon-url': `url("${process.env.PUBLIC_URL}/icons/post-fill.svg")`,
} as React.CSSProperties;
const analyticsReelViewsIconStyle = {
  '--analytics-metric-icon-url': `url("${process.env.PUBLIC_URL}/icons/video-two.svg")`,
} as React.CSSProperties;
const analyticsTrendGroupingIconStyle = {
  '--analytics-grouping-icon-url': `url("${process.env.PUBLIC_URL}/icons/sort-bold.svg")`,
} as React.CSSProperties;
const analyticsBestTimeStarIconStyle = {
  '--analytics-best-time-star-icon-url': `url("${process.env.PUBLIC_URL}/icons/star-filled.svg")`,
} as React.CSSProperties;
const analyticsRankIconStyle = (rank: number) => ({
  '--analytics-rank-icon-url': `url("${process.env.PUBLIC_URL}/icons/number-${rank}-small.svg")`,
} as React.CSSProperties);
const REACTION_META: Record<PostReactionType, {label: string; color: string}> = {
  like: {label: 'Likes', color: '#5b7cf2'},
  love: {label: 'Loves', color: '#773af6'},
  cry: {label: 'Cries', color: '#ffe338'},
  laugh: {label: 'Laughs', color: '#35ad6c'},
  celebrate: {label: 'Celebrates', color: '#1598ad'},
  fire: {label: 'Exciting', color: '#d94560'},
};
const REACTION_ORDER: PostReactionType[] = ['like', 'love', 'cry', 'laugh', 'celebrate', 'fire'];
const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const BEST_TIME_LABELS = ['Best time', 'Great time', 'Good time'];
const ENGAGEMENT_CHART_COLOR = '#667eea';
const PREVIEW_DATES = ['2026-07-16', '2026-07-17', '2026-07-18', '2026-07-19', '2026-07-20', '2026-07-21', '2026-07-22'];
const PREVIEW_TOTALS = [1, 2, 1, 4, 3, 6, 4];
const PREVIEW_TREND: CreatorAnalyticsTrendBucket[] = PREVIEW_DATES.map((date, index) => ({
  date,
  reactions: Math.max(0, PREVIEW_TOTALS[index] - 2),
  comments: index % 3 === 0 ? 1 : 0,
  reposts: 0,
  follows: index % 2,
  views: index % 3,
  profileViews: index % 2,
  postViews: index % 3 === 0 ? 1 : 0,
  reelViews: index % 4 === 0 ? 1 : 0,
  serviceClicks: index === 5 ? 1 : 0,
  postsPublished: index === 1 || index === 5 ? 1 : 0,
  total: PREVIEW_TOTALS[index],
}));
const PREVIEW_ANALYTICS: CreatorAnalytics = {
  range: '30d',
  generatedAt: new Date().toISOString(),
  dateRange: {startDate: '2026-06-23', endDate: '2026-07-22', days: 30, timeZone: 'UTC'},
  summary: {
    totalEngagements: {value: 1840, previousValue: 1280, changePercent: 43.8},
    profileViews: {value: 620, previousValue: 480, changePercent: 29.2},
    serviceClicks: {value: 86, previousValue: 52, changePercent: 65.4},
    newFollowers: {value: 124, previousValue: 94, changePercent: 31.9},
    postsPublished: {value: 9, previousValue: 7, changePercent: 28.6},
    totalFollowers: 5820, totalFollowing: 438, totalPosts: 72, totalReelViews: 14900,
  },
  trend: PREVIEW_TREND,
  topContent: [
    {
      id: 'preview-post', type: 'post', title: 'Behind the scenes from training day',
      createdAt: '2026-07-21T12:00:00.000Z', score: 320, reactionCount: 184,
      commentCount: 42, repostCount: 14, viewCount: 10, href: '/home', thumbnailUrl: null,
      trend: PREVIEW_DATES.map((date, index) => ({date, score: PREVIEW_TOTALS[index]})),
    },
    {
      id: 'preview-reel', type: 'reel', title: 'Portfolio reel highlight',
      createdAt: '2026-07-19T12:00:00.000Z', score: 244, reactionCount: 0,
      commentCount: 18, repostCount: 0, viewCount: 208, href: '/home', thumbnailUrl: null,
      trend: PREVIEW_DATES.map((date, index) => ({date, score: PREVIEW_TOTALS[PREVIEW_TOTALS.length - index - 1]})),
    },
  ],
  reactionBreakdown: [
    {type: 'like', count: 430}, {type: 'laugh', count: 120}, {type: 'cry', count: 18},
    {type: 'love', count: 208}, {type: 'celebrate', count: 94}, {type: 'fire', count: 176},
  ],
  engagementByHour: Array.from({length: 24}, (_value, hour) => ({hour, count: hour >= 18 && hour <= 21 ? 70 - Math.abs(20 - hour) * 14 : Math.max(0, (hour % 5) * 4)})),
  postingInsights: {
    bestTimes: [
      {weekday: 4, startHour: 18, endHour: 20, count: 92},
      {weekday: 2, startHour: 17, endHour: 19, count: 78},
      {weekday: 6, startHour: 11, endHour: 13, count: 64},
    ],
    activityByWeekday: [1, 2, 3, 4, 5, 6, 0].map((weekday, index) => ({weekday, count: [40, 49, 52, 81, 45, 42, 48][index]})),
  },
  followerGrowth: PREVIEW_DATES.map((date, index) => ({date, count: index % 2})),
  recommendations: [],
};

const formatCompactNumber = (value: number) => new Intl.NumberFormat('en-US', {notation: 'compact', maximumFractionDigits: value >= 10000 ? 0 : 1}).format(value);
const formatExactNumber = (value: number) => new Intl.NumberFormat('en-US').format(value);
const formatDateLabel = (value: string) => {
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-US', {month: 'short', day: 'numeric', timeZone: 'UTC'});
};
const formatContentDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
};
const formatHour = (hour: number) => {
  const normalized = hour % 24;
  if (normalized === 0) return '12 AM';
  if (normalized === 12) return '12 PM';
  return normalized > 12 ? `${normalized - 12} PM` : `${normalized} AM`;
};
const formatHourRange = (startHour: number, endHour: number) => `${formatHour(startHour)} – ${formatHour(endHour)}`;
const getChangeText = (metric: CreatorAnalyticsMetric, comparisonLabel: string) => {
  if (metric.changePercent === null) return metric.value > 0 ? 'New activity' : 'No prior activity';
  return `${Math.abs(metric.changePercent)}% vs previous ${comparisonLabel}`;
};
const isRequestCanceled = (error: unknown) => typeof error === 'object' && error !== null
  && ((error as {code?: unknown}).code === 'ERR_CANCELED' || (error as {name?: unknown}).name === 'CanceledError');
type SmoothChartPoint = {x: number; y: number};

const getSmoothChartPath = (points: SmoothChartPoint[]) => {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  return points.slice(0, -1).reduce((path, current, index) => {
    const previous = points[index - 1] || current;
    const next = points[index + 1];
    const afterNext = points[index + 2] || next;
    const minY = Math.min(current.y, next.y);
    const maxY = Math.max(current.y, next.y);
    const clampY = (value: number) => Math.max(minY, Math.min(maxY, value));
    const controlOneX = current.x + (next.x - previous.x) / 6;
    const controlTwoX = next.x - (afterNext.x - current.x) / 6;
    const controlOneY = clampY(current.y + (next.y - previous.y) / 6);
    const controlTwoY = clampY(next.y - (afterNext.y - current.y) / 6);

    return `${path} C ${controlOneX} ${controlOneY}, ${controlTwoX} ${controlTwoY}, ${next.x} ${next.y}`;
  }, `M ${points[0].x} ${points[0].y}`);
};

const Sparkline: React.FC<{values: number[]; color?: string; className?: string}> = ({values, color = '#6f36ff', className}) => {
  const width = 120; const height = 30; const inset = 2;
  const maximum = Math.max(1, ...values);
  const step = values.length > 1 ? (width - inset * 2) / (values.length - 1) : 0;
  const points = values.map((value, index) => ({
    x: inset + index * step,
    y: height - inset - value / maximum * (height - inset * 2),
  }));
  const line = getSmoothChartPath(points) || `M ${inset} ${height - inset} L ${width - inset} ${height - inset}`;

  return (
    <svg className={className || styles.sparkline} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={line} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

const PanelTitle: React.FC<{title: string; subtitle: string; action?: React.ReactNode; prominent?: boolean; iconStyle?: React.CSSProperties}> = ({title, subtitle, action, prominent = false, iconStyle}) => (
  <div className={`${styles.panelHeader} ${prominent ? styles.panelHeaderProminent : ''}`}>
    <div>
      <div className={styles.panelTitleRow}>
        {prominent ? (
          <span className={styles.pageHeaderIconTile} style={iconStyle || analyticsHeaderIconStyle} aria-hidden="true">
            <span className={styles.pageHeaderIcon} />
          </span>
        ) : null}
        <h2>{title}</h2>
      </div>
      <p>{subtitle}</p>
    </div>
    {action}
  </div>
);

const MetricCard: React.FC<{
  label: string; value: number; metric?: CreatorAnalyticsMetric; comparisonLabel: string;
  icon?: LucideIcon; iconStyle?: React.CSSProperties;
  tone: 'purple' | 'blue' | 'orange' | 'green' | 'teal' | 'violet' | 'pink'; series: number[];
}> = ({label, value, metric, comparisonLabel, icon: Icon, iconStyle, tone, series}) => {
  const changePercent = metric?.changePercent;
  const hasDirectionalChange = changePercent !== null && changePercent !== undefined;
  const isIncrease = hasDirectionalChange && changePercent >= 0;

  return (
    <article className={`${styles.metricCard} ${styles[`metricTone${tone[0].toUpperCase()}${tone.slice(1)}`]}`}>
      <div className={styles.metricTop}>
        <span className={styles.metricIcon}>
          {iconStyle
            ? <span className={styles.metricIconAsset} style={iconStyle} aria-hidden="true" />
            : Icon ? <Icon size={23} strokeWidth={2.2} aria-hidden="true" /> : null}
        </span>
        <div className={styles.metricCopy}><span className={styles.metricLabel}>{label}</span><strong className={styles.metricValue}>{formatCompactNumber(value)}</strong></div>
      </div>
      <span className={`${styles.metricChange} ${!hasDirectionalChange ? styles.metricNeutral : isIncrease ? styles.metricPositive : styles.metricNegative}`}>
        {hasDirectionalChange ? (
          <span
            className={styles.metricChangeIcon}
            style={isIncrease ? analyticsIncreaseIconStyle : analyticsDecreaseIconStyle}
            aria-hidden="true"
          />
        ) : null}
        <span className={styles.metricChangeText}>{metric ? getChangeText(metric, comparisonLabel) : 'All time'}</span>
      </span>
      <Sparkline values={series} color="var(--metric-color)" className={styles.metricSparkline} />
    </article>
  );
};

type TrendGranularity = 'daily' | 'weekly';
const aggregateWeeklyTrend = (trend: CreatorAnalyticsTrendBucket[]) => {
  const groups = new Map<string, CreatorAnalyticsTrendBucket>();
  trend.forEach((bucket) => {
    const date = new Date(`${bucket.date}T00:00:00.000Z`);
    date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
    const key = date.toISOString().slice(0, 10);
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, {...bucket, date: key});
    } else {
      (Object.keys(existing) as Array<keyof CreatorAnalyticsTrendBucket>).forEach((field) => {
        if (field !== 'date') (existing[field] as number) += bucket[field] as number;
      });
    }
  });
  return Array.from(groups.values());
};

const EngagementTrend: React.FC<{trend: CreatorAnalyticsTrendBucket[]}> = ({trend}) => {
  const [granularity, setGranularity] = React.useState<TrendGranularity>('daily');
  const [granularityMenuOpen, setGranularityMenuOpen] = React.useState(false);
  const [chartWidth, setChartWidth] = React.useState(680);
  const granularityControlRef = React.useRef<HTMLDivElement | null>(null);
  const chartWrapRef = React.useRef<HTMLDivElement | null>(null);
  const granularityButtonId = React.useId();
  const granularityMenuId = React.useId();
  const data = granularity === 'daily' ? trend : aggregateWeeklyTrend(trend);
  const width = chartWidth; const height = 228; const left = 38; const right = 12; const top = 12; const bottom = 34;
  const plotWidth = width - left - right; const plotHeight = height - top - bottom;
  const maximum = Math.ceil(Math.max(4, ...data.map((item) => item.total)) / 4) * 4;
  const points = data.map((item, index) => ({
    item,
    x: left + (data.length > 1 ? index * plotWidth / (data.length - 1) : plotWidth / 2),
    y: top + plotHeight - item.total / maximum * plotHeight,
  }));
  const line = getSmoothChartPath(points);
  const area = points.length ? `${line} L ${points[points.length - 1].x} ${top + plotHeight} L ${points[0].x} ${top + plotHeight} Z` : '';
  const labelEvery = Math.max(1, Math.ceil(data.length / 6));

  React.useEffect(() => {
    if (!granularityMenuOpen) return undefined;
    const closeFromOutside = (event: MouseEvent) => {
      if (!granularityControlRef.current?.contains(event.target as Node)) setGranularityMenuOpen(false);
    };
    document.addEventListener('mousedown', closeFromOutside);
    return () => document.removeEventListener('mousedown', closeFromOutside);
  }, [granularityMenuOpen]);

  React.useEffect(() => {
    const chartWrap = chartWrapRef.current;
    if (!chartWrap) return undefined;

    const updateChartWidth = () => {
      const nextWidth = Math.max(240, Math.round(chartWrap.getBoundingClientRect().width));
      setChartWidth((currentWidth) => currentWidth === nextWidth ? currentWidth : nextWidth);
    };

    updateChartWidth();
    window.addEventListener('resize', updateChartWidth);
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateChartWidth) : null;
    resizeObserver?.observe(chartWrap);

    return () => {
      window.removeEventListener('resize', updateChartWidth);
      resizeObserver?.disconnect();
    };
  }, []);

  const selectGranularity = (value: TrendGranularity) => {
    setGranularity(value);
    setGranularityMenuOpen(false);
  };

  return (
    <section className={`${styles.panel} ${styles.trendPanel}`}>
      <PanelTitle prominent iconStyle={analyticsTrendHeaderIconStyle} title="Engagement Trend" subtitle="Reactions, comments, reposts, follows, views, and service clicks." action={(
        <div className={styles.chartSelectWrap} ref={granularityControlRef}>
          <button
            id={granularityButtonId}
            type="button"
            className={styles.chartSelectButton}
            aria-haspopup="listbox"
            aria-expanded={granularityMenuOpen}
            aria-controls={granularityMenuOpen ? granularityMenuId : undefined}
            onClick={() => setGranularityMenuOpen((open) => !open)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                setGranularityMenuOpen(true);
              } else if (event.key === 'Escape') {
                setGranularityMenuOpen(false);
              }
            }}
          >
            <span className={styles.chartSelectIcon} style={analyticsTrendGroupingIconStyle} aria-hidden="true" />
            <span className={styles.chartSelectPrefix}>Group by:</span>
            <span className={styles.chartSelectValue}>{granularity === 'daily' ? 'Daily' : 'Weekly'}</span>
            <ChevronDown className={styles.chartSelectChevron} size={18} strokeWidth={2.5} aria-hidden="true" />
          </button>
          {granularityMenuOpen ? (
            <div id={granularityMenuId} className={styles.chartSelectMenu} role="listbox" aria-labelledby={granularityButtonId}>
              {(['daily', 'weekly'] as TrendGranularity[]).map((option) => {
                const selected = option === granularity;
                return (
                  <button
                    key={option}
                    type="button"
                    className={`${styles.chartSelectOption} ${selected ? styles.chartSelectOptionSelected : ''}`}
                    role="option"
                    aria-selected={selected}
                    onClick={() => selectGranularity(option)}
                  >
                    <span>{option === 'daily' ? 'Daily' : 'Weekly'}</span>
                    {selected ? <span className={styles.chartSelectCheck} aria-hidden="true" /> : null}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      )} />
      <div className={styles.lineChartWrap} ref={chartWrapRef}>
        <svg className={styles.lineChart} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Engagement trend chart">
          <defs><linearGradient id="engagementArea" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={ENGAGEMENT_CHART_COLOR} stopOpacity="0.24" /><stop offset="100%" stopColor={ENGAGEMENT_CHART_COLOR} stopOpacity="0" /></linearGradient></defs>
          {[0, 1, 2, 3, 4].map((step) => {
            const y = top + plotHeight * step / 4;
            return <g key={step}><line x1={left} x2={width - right} y1={y} y2={y} stroke="#cbd3df" strokeWidth="1.25" strokeDasharray="5 5" /><text x={left - 12} y={y + 4} textAnchor="end" className={styles.axisText}>{Math.round(maximum * (1 - step / 4))}</text></g>;
          })}
          {area ? <path d={area} fill="url(#engagementArea)" /> : null}
          {line ? <path data-chart-path="trend-line" d={line} fill="none" stroke={ENGAGEMENT_CHART_COLOR} strokeOpacity="1" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" /> : null}
          {points.map(({x, y, item}, index) => (
            <g key={item.date}>
              <circle cx={x} cy={y} r="3.5" fill={ENGAGEMENT_CHART_COLOR} stroke="#fff" strokeWidth="1.5"><title>{`${formatDateLabel(item.date)}: ${item.total} engagements`}</title></circle>
              {(index === 0 || index === points.length - 1 || index % labelEvery === 0) ? <text x={x} y={height - 8} textAnchor={index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'middle'} className={styles.axisText}>{formatDateLabel(item.date)}</text> : null}
            </g>
          ))}
        </svg>
      </div>
    </section>
  );
};

const ReactionMix: React.FC<{analytics: CreatorAnalytics}> = ({analytics}) => {
  const items = REACTION_ORDER.map((type) => analytics.reactionBreakdown.find((item) => item.type === type) || {type, count: 0});
  const total = items.reduce((sum, item) => sum + item.count, 0);
  let cursor = 0;
  const stops = items.map((item) => { const start = cursor; cursor += total ? item.count / total * 100 : 0; return `${REACTION_META[item.type].color} ${start}% ${cursor}%`; });
  const gradient = total ? `conic-gradient(${stops.join(', ')})` : 'conic-gradient(#ececf5 0 100%)';
  return (
    <section className={`${styles.panel} ${styles.reactionPanel}`}>
      <PanelTitle prominent iconStyle={analyticsReactionMixHeaderIconStyle} title="Reaction Mix" subtitle={`${formatExactNumber(total)} reactions in this range.`} />
      <div className={styles.reactionContent}>
        <div className={styles.donut} style={{background: gradient}} aria-label={`${total} total reactions`}><div className={styles.donutCenter}><strong>{formatCompactNumber(total)}</strong><span>Total</span></div></div>
        <div className={styles.reactionList}>{items.map((item) => { const meta = REACTION_META[item.type]; return <div key={item.type} className={styles.reactionRow}><span aria-hidden="true">{POST_REACTION_BY_TYPE[item.type].emoji}</span><strong>{meta.label}</strong><span>{formatExactNumber(item.count)}</span><span>{total ? Math.round(item.count / total * 100) : 0}%</span></div>; })}</div>
      </div>
    </section>
  );
};

const BestTimeToPost: React.FC<{analytics: CreatorAnalytics}> = ({analytics}) => {
  const maximum = Math.max(1, ...analytics.postingInsights.activityByWeekday.map((item) => item.count));
  return (
    <section className={`${styles.panel} ${styles.bestTimePanel}`}>
      <PanelTitle prominent iconStyle={analyticsBestTimeHeaderIconStyle} title="Best Time to Post" subtitle="Based on your audience's recent engagement." />
      {analytics.postingInsights.bestTimes.length ? (
        <div className={styles.bestTimeCards}>{analytics.postingInsights.bestTimes.map((item, index) => (
          <article key={item.weekday} className={`${styles.bestTimeCard} ${index === 0 ? styles.bestTimeCardPrimary : ''}`}>
            <div className={styles.bestTimeDay}><span>{index + 1}</span><strong>{WEEKDAY_NAMES[item.weekday]}</strong></div>
            <strong className={styles.bestTimeHours}>{formatHourRange(item.startHour, item.endHour)}</strong>
            <span className={styles.bestTimeBadge}><span className={styles.bestTimeBadgeIcon} style={analyticsBestTimeStarIconStyle} aria-hidden="true" />{BEST_TIME_LABELS[index]}</span>
          </article>
        ))}</div>
      ) : <div className={styles.insightEmpty}>Not enough engagement yet to recommend a posting window.</div>}
      <div className={styles.weekdaySection}><span className={styles.weekdayHeading}>Your activity in this range</span><div className={styles.weekdayGrid}>{analytics.postingInsights.activityByWeekday.map((item) => (
        <div key={item.weekday} className={styles.weekdayItem} title={`${WEEKDAY_NAMES[item.weekday]}: ${item.count} engagements`}><span>{WEEKDAY_NAMES[item.weekday]}</span><i><i style={{width: `${Math.max(item.count ? 12 : 0, item.count / maximum * 100)}%`}} /></i></div>
      ))}</div></div>
      <div className={styles.postingHint}><Lightbulb size={16} aria-hidden="true" /> Posting during these windows typically drives higher engagement.</div>
    </section>
  );
};

const ContentThumbnail: React.FC<{item: CreatorAnalyticsContentItem}> = ({item}) => {
  const [failed, setFailed] = React.useState(false);
  const imageUrl = getAssetUrl(item.thumbnailUrl || undefined);
  return imageUrl && !failed ? <img src={imageUrl} alt="" className={styles.contentThumbnail} onError={() => setFailed(true)} /> : <span className={styles.contentThumbnailPlaceholder} aria-hidden="true">{item.type === 'reel' ? <Play size={22} /> : <ImageIcon size={22} />}</span>;
};

const TopContent: React.FC<{items: CreatorAnalyticsContentItem[]; onOpenContent: (item: CreatorAnalyticsContentItem) => void}> = ({items, onOpenContent}) => (
  <section className={`${styles.panel} ${styles.topContentPanel}`}>
    <PanelTitle prominent iconStyle={analyticsTopContentHeaderIconStyle} title="Top Content" subtitle="Ranked by engagement score." />
    {items.length ? <div className={styles.topContentList}>{items.slice(0, 4).map((item, index) => (
      <button key={`${item.type}-${item.id}`} type="button" className={styles.topContentRow} onClick={() => onOpenContent(item)}>
        <span className={styles.rank} style={analyticsRankIconStyle(index + 1)} role="img" aria-label={`Rank ${index + 1}`} /><ContentThumbnail item={item} />
        <span className={styles.contentMain}><strong>{item.title}</strong><span>{item.type === 'post' ? 'Post' : 'Reel'} · {formatContentDate(item.createdAt)}</span></span>
        <span className={styles.contentScore}><strong>{formatCompactNumber(item.score)}</strong><span>Score</span></span>
        <Sparkline values={item.trend.map((point) => point.score)} color="#35ad78" className={styles.contentSparkline} />
      </button>
    ))}</div> : <div className={styles.insightEmpty}>No top content yet.</div>}
  </section>
);

const DashboardContent: React.FC<{analytics: CreatorAnalytics; isPreview?: boolean; onOpenContent: (item: CreatorAnalyticsContentItem) => void}> = ({analytics, isPreview = false, onOpenContent}) => {
  const comparison = analytics.range === 'custom' ? `${analytics.dateRange.days} days` : analytics.range.toUpperCase();
  const series = (key: keyof CreatorAnalyticsTrendBucket) => analytics.trend.map((bucket) => Number(bucket[key]) || 0);
  const empty = !analytics.summary.totalEngagements.value && !analytics.topContent.length;
  return (
    <div className={`${styles.dashboardContent} ${isPreview ? styles.previewBlurred : ''}`}>
      <div className={styles.metricGrid}>
        <MetricCard label="Total Engagements" value={analytics.summary.totalEngagements.value} metric={analytics.summary.totalEngagements} comparisonLabel={comparison} iconStyle={analyticsTrendUpIconStyle} tone="blue" series={series('total')} />
        <MetricCard label="Profile Views" value={analytics.summary.profileViews.value} metric={analytics.summary.profileViews} comparisonLabel={comparison} iconStyle={analyticsUserGroupIconStyle} tone="purple" series={series('profileViews')} />
        <MetricCard label="Service Clicks" value={analytics.summary.serviceClicks.value} metric={analytics.summary.serviceClicks} comparisonLabel={comparison} iconStyle={analyticsServiceClicksIconStyle} tone="orange" series={series('serviceClicks')} />
        <MetricCard label="New Followers" value={analytics.summary.newFollowers.value} metric={analytics.summary.newFollowers} comparisonLabel={comparison} iconStyle={analyticsNewFollowersIconStyle} tone="green" series={series('follows')} />
        <MetricCard label="Posts Published" value={analytics.summary.postsPublished.value} metric={analytics.summary.postsPublished} comparisonLabel={comparison} iconStyle={analyticsPostsPublishedIconStyle} tone="teal" series={series('postsPublished')} />
        <MetricCard label="Reel Views" value={analytics.summary.totalReelViews} comparisonLabel={comparison} iconStyle={analyticsReelViewsIconStyle} tone="pink" series={series('reelViews')} />
      </div>
      {empty ? <div className={styles.emptyBanner}><strong>No analytics yet</strong><span>Your dashboard will fill in as people engage with your work.</span></div> : null}
      <div className={styles.dashboardGrid}><EngagementTrend trend={analytics.trend} /><ReactionMix analytics={analytics} /><BestTimeToPost analytics={analytics} /><TopContent items={analytics.topContent} onOpenContent={onOpenContent} /></div>
    </div>
  );
};

const AnalyticsLockedPreview: React.FC = () => {
  const navigate = useNavigate();
  return <div className={styles.previewWrap}><DashboardContent analytics={PREVIEW_ANALYTICS} isPreview onOpenContent={() => undefined} /><div className={styles.previewOverlay}><span className={styles.lockBadge}><LockKeyhole size={22} aria-hidden="true" /></span><h2>Unlock Creator Analytics</h2><p>See top content, engagement timing, follower growth, and service click signals with XPSR Pro.</p><button type="button" className={styles.primaryButton} onClick={() => navigate('/settings?upgrade=1')}>Upgrade to Pro</button></div></div>;
};

const csvCell = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
export const buildAnalyticsCsv = (analytics: CreatorAnalytics) => {
  const rows: unknown[][] = [];
  const section = (title: string, headers: string[], values: unknown[][]) => { if (rows.length) rows.push([]); rows.push([title], headers, ...values); };
  rows.push(['Creator Analytics Report'], ['Start Date', analytics.dateRange.startDate], ['End Date', analytics.dateRange.endDate], ['Time Zone', analytics.dateRange.timeZone], ['Generated At', analytics.generatedAt]);
  section('Summary', ['Metric', 'Value', 'Previous Value', 'Change Percent'], [
    ['Total Engagements', analytics.summary.totalEngagements.value, analytics.summary.totalEngagements.previousValue, analytics.summary.totalEngagements.changePercent],
    ['Profile Views', analytics.summary.profileViews.value, analytics.summary.profileViews.previousValue, analytics.summary.profileViews.changePercent],
    ['Service Clicks', analytics.summary.serviceClicks.value, analytics.summary.serviceClicks.previousValue, analytics.summary.serviceClicks.changePercent],
    ['New Followers', analytics.summary.newFollowers.value, analytics.summary.newFollowers.previousValue, analytics.summary.newFollowers.changePercent],
    ['Posts Published', analytics.summary.postsPublished.value, analytics.summary.postsPublished.previousValue, analytics.summary.postsPublished.changePercent],
    ['Reel Views (All Time)', analytics.summary.totalReelViews, '', ''],
  ]);
  section('Engagement Trend', ['Date', 'Total', 'Reactions', 'Comments', 'Reposts', 'Follows', 'Profile Views', 'Post Views', 'Reel Views', 'Service Clicks', 'Posts Published'], analytics.trend.map((item) => [item.date, item.total, item.reactions, item.comments, item.reposts, item.follows, item.profileViews, item.postViews, item.reelViews, item.serviceClicks, item.postsPublished]));
  section('Reaction Mix', ['Reaction', 'Count'], REACTION_ORDER.map((type) => [REACTION_META[type].label, analytics.reactionBreakdown.find((item) => item.type === type)?.count || 0]));
  section('Best Times', ['Weekday', 'Start Hour', 'End Hour', 'Engagements'], analytics.postingInsights.bestTimes.map((item) => [WEEKDAY_NAMES[item.weekday], formatHour(item.startHour), formatHour(item.endHour), item.count]));
  section('Weekday Activity', ['Weekday', 'Engagements'], analytics.postingInsights.activityByWeekday.map((item) => [WEEKDAY_NAMES[item.weekday], item.count]));
  section('Top Content', ['Rank', 'Type', 'Title', 'Created At', 'Score', 'Views', 'Comments', 'Reposts', 'Link'], analytics.topContent.map((item, index) => [index + 1, item.type, item.title, item.createdAt, item.score, item.viewCount, item.commentCount, item.repostCount, item.href]));
  return `\uFEFF${rows.map((row) => row.map(csvCell).join(',')).join('\r\n')}`;
};
const localDateKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const validateCustomRange = (startDate: string, endDate: string, today: string) => {
  if (!startDate || !endDate) return 'Choose both a start and end date.';
  if (startDate > endDate) return 'Start date must be on or before end date.';
  if (endDate > today) return 'End date cannot be in the future.';
  const days = Math.round((new Date(`${endDate}T00:00:00.000Z`).getTime() - new Date(`${startDate}T00:00:00.000Z`).getTime()) / 86400000) + 1;
  return days > 90 ? 'Custom ranges cannot exceed 90 days.' : null;
};

const Analytics: React.FC = () => {
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.user.currentUser);
  const isProUser = isProPlanTier(user?.planTier);
  const timeZone = React.useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC', []);
  const today = React.useMemo(() => localDateKey(new Date()), []);
  const initialStart = React.useMemo(() => { const date = new Date(); date.setDate(date.getDate() - 29); return localDateKey(date); }, []);
  const [selection, setSelection] = React.useState<CreatorAnalyticsSelection>('30d');
  const [draftStartDate, setDraftStartDate] = React.useState(initialStart);
  const [draftEndDate, setDraftEndDate] = React.useState(today);
  const [calendarOpen, setCalendarOpen] = React.useState(false);
  const [calendarError, setCalendarError] = React.useState<string | null>(null);
  const [analytics, setAnalytics] = React.useState<CreatorAnalytics | null>(null);
  const [loading, setLoading] = React.useState(isProUser);
  const [error, setError] = React.useState<string | null>(null);
  const [upgradeRequired, setUpgradeRequired] = React.useState(false);
  const [reloadKey, setReloadKey] = React.useState(0);
  const calendarRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const outside = (event: MouseEvent) => { if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) setCalendarOpen(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setCalendarOpen(false); };
    document.addEventListener('mousedown', outside); window.addEventListener('keydown', escape);
    return () => { document.removeEventListener('mousedown', outside); window.removeEventListener('keydown', escape); };
  }, []);
  React.useEffect(() => {
    if (!isProUser || !token) { setAnalytics(null); setLoading(false); setError(null); setUpgradeRequired(false); return; }
    let mounted = true;
    const controller = new AbortController();
    const load = async () => {
      setLoading(true); setError(null); setUpgradeRequired(false);
      try {
        const next = await analyticsApi.getCreatorAnalytics(token, selection, timeZone, controller.signal);
        if (mounted) setAnalytics(next);
      } catch (loadError) {
        if (isRequestCanceled(loadError) || !mounted) return;
        if (loadError instanceof AnalyticsApiError && loadError.code === 'UPGRADE_REQUIRED') { setUpgradeRequired(true); setError(null); }
        else setError(loadError instanceof Error ? loadError.message : 'Failed to load analytics');
        setAnalytics(null);
      } finally { if (mounted) setLoading(false); }
    };
    void load();
    return () => { mounted = false; controller.abort(); };
  }, [isProUser, reloadKey, selection, timeZone, token]);

  const applyCustomRange = () => {
    const nextError = validateCustomRange(draftStartDate, draftEndDate, today);
    if (nextError) { setCalendarError(nextError); return; }
    setSelection({startDate: draftStartDate, endDate: draftEndDate}); setCalendarError(null); setCalendarOpen(false);
  };
  const exportReport = () => {
    if (!analytics) return;
    const url = URL.createObjectURL(new Blob([buildAnalyticsCsv(analytics)], {type: 'text/csv;charset=utf-8'}));
    const link = document.createElement('a'); link.href = url; link.download = `creator-analytics-${analytics.dateRange.startDate}-to-${analytics.dateRange.endDate}.csv`;
    document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageContent}>
        <div className={styles.pageSectionShell}>
      {!error && !loading ? <div className={styles.pageHeader}>
        <div className={styles.pageHeaderHeading}>
          <div className={styles.pageHeaderTitleRow}>
            <span
              className={styles.pageHeaderIconTile}
              style={analyticsHeaderIconStyle}
              aria-hidden="true"
            >
              <span className={styles.pageHeaderIcon} />
            </span>
            <h1>Creator Analytics</h1>
          </div>
          <p>Track content performance, audience growth, and profile demand.</p>
        </div>
        {isProUser ? <div className={styles.headerControls}>
          <div className={styles.rangeControl} role="group" aria-label="Analytics range">{RANGE_OPTIONS.map((option) => <button key={option.value} type="button" className={selection === option.value ? styles.rangeButtonActive : styles.rangeButton} onClick={() => { setSelection(option.value); setCalendarOpen(false); }}>{option.label}</button>)}</div>
          <div className={styles.calendarControl} ref={calendarRef}>
            <button type="button" className={`${styles.iconButton} ${typeof selection === 'object' ? styles.iconButtonActive : ''}`} aria-label="Choose custom date range" aria-expanded={calendarOpen} onClick={() => { setCalendarOpen((open) => !open); setCalendarError(null); }}><span className={styles.calendarButtonIcon} style={analyticsCalendarIconStyle} aria-hidden="true" /></button>
              {calendarOpen ? <div className={styles.calendarPopover} role="dialog" aria-label="Custom analytics date range"><strong>Custom date range</strong><label>Start date<input type="date" style={analyticsDateInputIconStyle} value={draftStartDate} max={draftEndDate || today} onChange={(event) => setDraftStartDate(event.target.value)} /></label><label>End date<input type="date" style={analyticsDateInputIconStyle} value={draftEndDate} min={draftStartDate} max={today} onChange={(event) => setDraftEndDate(event.target.value)} /></label>{calendarError ? <p role="alert">{calendarError}</p> : null}<div className={styles.calendarActions}><button type="button" onClick={() => setCalendarOpen(false)}>Cancel</button><button type="button" onClick={applyCustomRange}>Apply</button></div></div> : null}
          </div>
          <button type="button" className={styles.exportButton} onClick={exportReport} disabled={!analytics || loading || Boolean(error)}><span className={styles.exportButtonIcon} style={analyticsExportIconStyle} aria-hidden="true" /> Export Report</button>
        </div> : null}
      </div> : null}
      {!isProUser || upgradeRequired ? <AnalyticsLockedPreview /> : loading ? (
        <div className={styles.loadingState} role="status">
          <span className={styles.loadingSpinner} aria-hidden="true" />
          <h2 className={styles.loadingStateTitle}>Loading analytics</h2>
          <p className={styles.loadingStateDescription}>
            We&apos;re loading your creator analytics data from the database.
          </p>
        </div>
      ) : error ? (
        <div className={styles.errorState} role="alert">
          <ProfileWarningIcon />
          <h2 className={styles.errorStateTitle}>Unable to load analytics</h2>
          <p className={styles.errorStateDescription}>{error}</p>
          <button
            type="button"
            className={styles.errorStateRetryButton}
            onClick={() => setReloadKey((key) => key + 1)}
          >
            Try Again
          </button>
        </div>
      ) : analytics ? <DashboardContent analytics={analytics} onOpenContent={(item) => navigate(item.href)} /> : null}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
